// src/pages/Admin/Views/LeadPricingSetup/ConditionDefinitionsPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { 
    getPricingConditionDefinitions, 
    createPricingConditionDefinition,
    updatePricingConditionDefinition,
    deletePricingConditionDefinition // Add if you implement the delete button
} from '../../../../services/pricingApi';
import { Button } from '../../../../components/ui/button';
import ConditionDefinitionList from '../../../../components/Admin/Category/Pricing/ConditionDefinitionList'; // Import new component
import ConditionDefinitionForm from '../../../../components/Admin/Category/Pricing/ConditionDefinitionForm';   // Import new component
// import { toast } from 'react-toastify'; // Optional: for notifications

export default function ConditionDefinitionsPage() {
    const [definitions, setDefinitions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [editingDefinition, setEditingDefinition] = useState(null);

    const fetchDefinitions = useCallback(async (showInactive = false) => {
        setIsLoading(true);
        setError('');
        try {
            const response = await getPricingConditionDefinitions({ limit: 200, active_only: !showInactive });
            setDefinitions(response.data);
        } catch (err) {
            const errorMsg = `Failed to fetch condition definitions: ${err.response?.data?.detail || err.message}`;
            setError(errorMsg);
            // toast.error(errorMsg);
            console.error("Fetch definitions error:", err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDefinitions(true); // Load all (active and inactive) initially for admin view
    }, [fetchDefinitions]);

    const handleSave = async (formData) => {
        setIsLoading(true);
        setError('');
        try {
            if (editingDefinition) {
                await updatePricingConditionDefinition(editingDefinition.id, formData);
                // toast.success("Condition definition updated successfully!");
            } else {
                await createPricingConditionDefinition(formData);
                // toast.success("Condition definition created successfully!");
            }
            setShowForm(false);
            setEditingDefinition(null);
            fetchDefinitions(true); // Refresh list
        } catch (err) {
             const errorMsg = `Failed to save condition definition: ${err.response?.data?.detail || err.message}`;
             setError(errorMsg);
             // toast.error(errorMsg);
             console.error("Save definition error:", err)
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (definition) => {
        setEditingDefinition(definition);
        setShowForm(true);
    };
    
    const handleAddNew = () => {
        setEditingDefinition(null); // Ensure form is for creation
        setShowForm(true);
    };

    const handleDelete = async (definitionId) => {
        // Implement a confirmation dialog here before deleting
        if (window.confirm(`Are you sure you want to delete this condition definition? This action might affect existing pricing rules if not handled carefully on the backend.`)) {
            setIsLoading(true);
            setError('');
            try {
                await deletePricingConditionDefinition(definitionId);
                // toast.success("Condition definition deleted successfully!");
                fetchDefinitions(true); // Refresh list
            } catch (err) {
                const errorMsg = `Failed to delete condition definition: ${err.response?.data?.detail || err.message}`;
                setError(errorMsg);
                // toast.error(errorMsg);
                 console.error("Delete definition error:", err)
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <div className="p-4 md:p-6">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 pb-4 border-b">
                <h1 className="text-2xl font-bold text-gray-800">Manage Pricing Condition Definitions</h1>
                {!showForm && (
                    <Button onClick={handleAddNew} variant="primary" size="md">
                        Add New Definition
                    </Button>
                )}
            </div>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
                    <strong className="font-bold">Error: </strong>
                    <span className="block sm:inline">{error}</span>
                </div>
            )}

            {showForm ? (
                <div className="max-w-2xl mx-auto"> {/* Center the form */}
                    <ConditionDefinitionForm
                        initialData={editingDefinition}
                        onSave={handleSave}
                        onCancel={() => { setShowForm(false); setEditingDefinition(null); }}
                        isLoading={isLoading}
                    />
                </div>
            ) : isLoading ? (
                <p className="text-gray-600">Loading condition definitions...</p>
            ) : (
                <ConditionDefinitionList 
                    items={definitions} 
                    onEdit={handleEdit} 
                    // onDelete={handleDelete} // Uncomment if you implement the delete button in ConditionDefinitionList
                />
            )}
        </div>
    );
}